import { __decorate } from "tslib";
import { AutoEncoder, DateDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { SimpleError } from '@simonbackx/simple-errors';
import { encodeSortList, SortListDecoder } from './SortList.js';
export class StamhoofdFilterJSONDecoder {
    static encode(filter, context) {
        return JSON.stringify(StamhoofdFilterDecoder.encode(filter, context));
    }
    static decode(data) {
        const str = data.string;
        try {
            const decoded = JSON.parse(str);
            return StamhoofdFilterDecoder.decode(data.clone({ data: decoded, field: data.currentField, context: data.context }));
        }
        catch (e) {
            throw new SimpleError({
                code: 'invalid_field',
                message: `Expected JSON at ${data.currentField}`,
                field: data.currentField,
            });
        }
    }
}
export class StamhoofdFilterDecoder {
    static encode(filter, context) {
        // We need to convert all Date objects to something recornizable so we can decode them as Dates too
        // We'll use magic objects for this: { $: '$date', value: unixtimeinms }
        if (filter instanceof Date) {
            return { $: '$date', value: filter.encode(context) };
        }
        if (filter === null) {
            return null;
        }
        if (Array.isArray(filter)) {
            return filter.map(f => this.encode(f, context));
        }
        if (typeof filter === 'object') {
            // Loop and replace all keys
            const c = {};
            for (const [key, value] of Object.entries(filter)) {
                c[key] = this.encode(value, context);
            }
            return c;
        }
        return filter;
    }
    static decode(data) {
        const value = data.value;
        if (value === null) {
            return null;
        }
        if (Array.isArray(value)) {
            return value.map((v, index) => this.decode(data.clone({
                data: v,
                field: data.addToCurrentField(index),
                context: data.context,
            })));
        }
        if (typeof value === 'object') {
            // Check if it's a date
            if ('$' in value && value.$ === '$date' && 'value' in value) {
                return DateDecoder.decode(data.clone({
                    data: value.value,
                    field: data.addToCurrentField('value'),
                    context: data.context,
                }));
            }
            // Loop and replace all keys
            const c = {};
            for (const [key, v] of Object.entries(value)) {
                c[key] = this.decode(data.clone({
                    data: v,
                    field: data.addToCurrentField(key),
                    context: data.context,
                }));
            }
            return c;
        }
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return value;
        }
        throw new SimpleError({
            code: 'invalid_field',
            message: `Invalid filter at ${data.currentField}`,
            field: data.currentField,
        });
    }
}
export class CountResponse extends AutoEncoder {
    count;
}
__decorate([
    field({ decoder: IntegerDecoder })
], CountResponse.prototype, "count", void 0);
export class CountFilteredRequest {
    filter;
    search;
    constructor(data) {
        this.filter = data.filter ?? null;
        this.search = data.search ?? null;
    }
    static decode(data) {
        return new CountFilteredRequest({
            filter: data.optionalField('filter')?.nullable(StamhoofdFilterJSONDecoder),
            search: data.optionalField('search')?.nullable(StringDecoder),
        });
    }
    encode(context) {
        return {
            filter: this.filter ? StamhoofdFilterJSONDecoder.encode(this.filter, context) : undefined,
            search: this.search ?? undefined,
        };
    }
}
export class LimitedFilteredRequest {
    /**
     * This is the base filter
     */
    filter;
    /**
     * This is a filter than get extended to fetch the next page
     */
    pageFilter;
    sort;
    limit;
    search;
    constructor(data) {
        this.filter = data.filter ?? null;
        this.pageFilter = data.pageFilter ?? null;
        this.sort = data.sort ?? [];
        this.limit = data.limit;
        this.search = data.search ?? null;
    }
    static decode(data) {
        return new LimitedFilteredRequest({
            filter: data.optionalField('filter')?.nullable(StamhoofdFilterJSONDecoder),
            pageFilter: data.optionalField('pageFilter')?.nullable(StamhoofdFilterJSONDecoder),
            sort: data.field('sort').decode(SortListDecoder),
            limit: data.field('limit').integer,
            search: data.optionalField('search')?.nullable(StringDecoder),
        });
    }
    encode(context) {
        return {
            filter: this.filter ? StamhoofdFilterJSONDecoder.encode(this.filter, context) : undefined,
            pageFilter: this.pageFilter ? StamhoofdFilterJSONDecoder.encode(this.pageFilter, context) : undefined,
            sort: encodeSortList(this.sort),
            limit: this.limit,
            search: this.search ?? undefined,
        };
    }
}
//# sourceMappingURL=FilteredRequest.js.map